import { Injectable } from '@angular/core';
import { AppLaunchResult } from '@office-start/launcher-types';
import { OfficeRoute } from '../constants/office-route.enum';
import { KnownReferralPlatform } from '../instrumentation/instrumentation.service';
import { Workload } from '../models/workload.model';
import { LocalAppLauncherService } from './local-app-launcher.service';
import { OfficeRouteService } from './office-route.service';
import { LaunchAction, UrlLauncherService } from './url-launcher.service';

export type PostLaunchCallback = (success: boolean, platform?: KnownReferralPlatform, nav?: boolean, uiHost?: boolean) => void;
@Injectable({
    providedIn: 'root'
})
export class AppLauncherService {

    constructor(
        private readonly localAppLauncherService: LocalAppLauncherService,
        private readonly urlLauncherService: UrlLauncherService,
        private readonly officeRouteService: OfficeRouteService
    ) { }
    /**
     *Launches the app on the appropriate platform.
     * If this is in the context of the PWA app:
     *     - If a native app exists, it is launched.
     *     - If a native app doesn't exist or there was an error launching it:
     *         - If the app supports in-app navigation, then navigates to the corresponding route
     *         - If the app doesn't support in-app navigation, then it opens the app in the browser.
     * If this is in the context of www.office.com:
     *     - If the app supports in-app navigation, then navigates to the corresponding route
     *     - If the app doesn't support in-app navigation, then it opens the app in a new tab (does not call preventDefault).
     * If this is in the context of Edge NTP, the app is opened in the same tab (does not call preventDefault).
     */
    public launchApp(workload: Workload, event: Event, action: LaunchAction, defaultLinkBehaviorDisabled: boolean = false): Promise<AppLaunchResult> {
        // Launch native Office app if available on the machine
        const prelaunchCallback = (event && !event.defaultPrevented && !defaultLinkBehaviorDisabled) ? () => event.preventDefault() : null;
        return this.localAppLauncherService.launchOfficeApp(workload.Text, '', prelaunchCallback).then(success => {
            if (!success) {
                return this.launchOfficeWebApp(workload, event, true, action);
            }

            return 'Native';
        }).catch(() => this.launchOfficeWebApp(workload, event, defaultLinkBehaviorDisabled, action));
    }
    private launchOfficeWebApp(workload: Workload, event: Event, defaultLinkBehaviorDisabled: boolean, action: LaunchAction): Promise<AppLaunchResult> {
        if (workload.InAppNav && workload.FileType) {
            if (event && !event.defaultPrevented && !defaultLinkBehaviorDisabled) {
                event.preventDefault();
            }
            this.officeRouteService.navigateTo({ route: OfficeRoute.Start, app: workload.FileType });
            return Promise.resolve('StartPage');
        } else if (defaultLinkBehaviorDisabled || !event || event.defaultPrevented) {
            return this.urlLauncherService.openUrl(workload.Url, action).then(() => 'Web', () => undefined);
        }
        return Promise.resolve('Web');
    }
}
